import { bind, EPlayerType, getVideoPlaybackQuality } from '@streaming/mouflon-utils';
import { debounce } from 'lodash';
import { DebouncedFunc } from 'lodash-es';

import { IStreamConfig } from '@customTypes/common';
import { EMetricsEvents, TMetricsEventPayload } from '@metrics/Metrics.events';
import { IDimensions } from '@metrics/VideoElementMetrics';
import { EPlayersEvents, TPlayersEventsPayload } from '@players/Players.events';
import { TMMPEventBus } from '@utils/EventBus';

const refreshInterval: number = 1000;

interface IVideoElementMetrics {
  videoResolution: string;
  elementResolution: string;
  dprElementResolution: string;
  videoAspectRatio: string;
  elementAspectRatio: string;
  fps: string;
  framesCount: string;
}
interface IDebugPanelRowOptions {
  labelText: string;
  rowStyle?: Partial<CSSStyleDeclaration>;
  valueContent?: {
    innerHTML?: string;
    style?: Partial<CSSStyleDeclaration>;
  };
}
interface IRowPanel {
  setRowValue(html: string): void;
  appendRowChild(child: Node): void;
  getRowChildren(): NodeListOf<ChildNode>;
  addRowEventListener<K extends keyof HTMLElementEventMap>(
    type: K,
    listener: (this: HTMLDivElement, ev: HTMLElementEventMap[K]) => unknown,
    options?: boolean | AddEventListenerOptions,
  ): void;
  removeRowEventListener<K extends keyof HTMLElementEventMap>(
    type: K,
    listener: (this: HTMLDivElement, ev: HTMLElementEventMap[K]) => unknown,
    options?: boolean | EventListenerOptions,
  ): void;
}
export class DebugPanel {
  private readonly _eventBusInstance: TMMPEventBus;
  private readonly _onVideoElementUpdatedListener: string;
  private readonly _onPlayerTypesAvailableListener: string;
  private readonly _onPlayerTypeChangedListener: string;
  private readonly _onStreamConfigUpdatedListener: string;
  private readonly _onLatencyEstimationAvailableListener: string;
  private readonly _onUpscaleCalculatedListener: string;
  private readonly _onVideoElementDimensionsCalculatedListener: string;
  private readonly _panelContainer: HTMLDivElement = document.createElement('div');
  private readonly _resolutionRow: IRowPanel = this._createPanelRow({ labelText: 'Resolution:' });
  private readonly _aspectRatioRow: IRowPanel = this._createPanelRow({
    labelText: 'Aspect ratio:',
  });
  private readonly _fpsRow: IRowPanel = this._createPanelRow({
    labelText: 'FPS:',
  });
  private readonly _latencyRow: IRowPanel = this._createPanelRow({
    labelText: 'Latency estimation:',
  });
  private readonly _upscaleRow: IRowPanel = this._createPanelRow({
    labelText: 'Upscale factor:',
  });
  private readonly _droppedFramesRow: IRowPanel = this._createPanelRow({
    labelText: 'Frames (dropped/total):',
  });
  private readonly _turnServersRow: IRowPanel = this._createPanelRow({
    labelText: 'TURN servers:',
    valueContent: {
      innerHTML: 'Disable TURN Servers',
      style: {
        padding: '5px 10px',
        border: '1px solid',
        borderRadius: '5px',
      },
    },
  });
  private readonly _playerTypesRow: IRowPanel = this._createPanelRow({
    labelText: 'Players:',
    valueContent: {
      style: {
        display: 'flex',
      },
    },
  });
  private readonly _streamOverridesRow: IRowPanel = this._createPanelRow({
    labelText: 'Overrides:',
    rowStyle: {
      display: 'flex',
      alignItems: 'center',
    },
  });

  private readonly _refreshInterval: ReturnType<typeof global.setInterval>;
  private readonly _domainOverrideInput: HTMLInputElement = document.createElement('input');
  private readonly _streamNameOverrideInput: HTMLInputElement = document.createElement('input');

  private _videoElement: HTMLVideoElement | null = null;
  private _prevFramesCount: number = -1;
  private _prevFramesCountTs: number = -1;
  private _availablePlayerTypes: EPlayerType[] = [];
  private _currentPlayerType: EPlayerType | null = null;
  private _shouldUseTURNServersState: boolean = true;
  private _domainOverride: string = '';
  private _lastPartOverride: string = '';
  private _currentStreamConfig: IStreamConfig | null = null;
  private _lastKnownVideoElementDimensions: IDimensions = { width: 0, height: 0 };

  constructor(eventBusInstance: TMMPEventBus) {
    this._eventBusInstance = eventBusInstance;
    this._onVideoElementUpdatedListener = eventBusInstance.subscribe(
      EPlayersEvents.VIDEO_ELEMENT_UPDATED,
      this._onVideoElementUpdated,
    );
    this._onPlayerTypesAvailableListener = eventBusInstance.subscribe(
      EPlayersEvents.PLAYER_TYPES_AVAILABLE,
      this._onPlayerTypesAvailable,
    );
    this._onPlayerTypeChangedListener = eventBusInstance.subscribe(
      EPlayersEvents.PLAYER_TYPE_CHANGED,
      this._onPlayerTypeChanged,
    );
    this._onStreamConfigUpdatedListener = eventBusInstance.subscribe(
      EPlayersEvents.STREAM_CONFIG_UPDATED,
      this._onStreamConfigUpdated,
    );
    this._onLatencyEstimationAvailableListener = eventBusInstance.subscribe(
      EPlayersEvents.LATENCY_ESTIMATION_AVAILABLE,
      this._onLatencyEstimationAvailable,
    );
    this._onUpscaleCalculatedListener = eventBusInstance.subscribe(
      EMetricsEvents.UPSCALE_CALCULATED,
      this._onUpscaleCalculated,
    );
    this._onVideoElementDimensionsCalculatedListener = eventBusInstance.subscribe(
      EMetricsEvents.VIDEO_ELEMENT_DIMENSIONS_CALCULATED,
      this._onVideoElementDimensionsCalculated,
    );

    this._streamOverridesRow.appendRowChild(this._domainOverrideInput);
    this._streamOverridesRow.appendRowChild(this._streamNameOverrideInput);

    const domainOverrideDebounce: DebouncedFunc<typeof this._onDomainOverrideChanged> = debounce(
      this._onDomainOverrideChanged,
      200,
    );
    const lastPartOverrideDebounce: DebouncedFunc<typeof this._onlastPartOverrideChanged> =
      debounce(this._onlastPartOverrideChanged, 200);

    this._domainOverrideInput.addEventListener('change', domainOverrideDebounce);
    this._streamNameOverrideInput.addEventListener('change', lastPartOverrideDebounce);

    this._panelContainer.style.display = 'flex';
    this._panelContainer.style.fontSize = '.625rem';
    this._panelContainer.style.flexDirection = 'column';

    this._domainOverrideInput.style.background = 'inherit';
    this._domainOverrideInput.style.color = 'inherit';
    this._domainOverrideInput.style.marginRight = '10px';
    this._domainOverrideInput.style.border = '1px solid';

    this._streamNameOverrideInput.style.background = 'inherit';
    this._streamNameOverrideInput.style.color = 'inherit';
    this._streamNameOverrideInput.style.border = '1px solid';

    this._turnServersRow.addRowEventListener('click', this._toggleTURNServers);
    this._refreshInterval = setInterval(this._refreshDebugData, refreshInterval);
  }

  public destroy(): void {
    this._eventBusInstance.unsubscribe(this._onPlayerTypesAvailableListener);
    this._eventBusInstance.unsubscribe(this._onVideoElementUpdatedListener);
    this._eventBusInstance.unsubscribe(this._onPlayerTypeChangedListener);
    this._eventBusInstance.unsubscribe(this._onStreamConfigUpdatedListener);
    this._eventBusInstance.unsubscribe(this._onLatencyEstimationAvailableListener);
    this._eventBusInstance.unsubscribe(this._onUpscaleCalculatedListener);
    this._eventBusInstance.unsubscribe(this._onVideoElementDimensionsCalculatedListener);
    clearInterval(this._refreshInterval);
  }

  @bind
  private _onDomainOverrideChanged(): void {
    this._domainOverride = this._domainOverrideInput.value;
    this._rebuildStreamConfig();
  }

  @bind
  private _onlastPartOverrideChanged(): void {
    this._lastPartOverride = this._streamNameOverrideInput.value;
    this._rebuildStreamConfig();
  }

  private _rebuildStreamConfig(): void {
    if (!this._currentStreamConfig) {
      return;
    }
    const config: IStreamConfig = { ...this._currentStreamConfig };

    if (
      ((this._currentPlayerType === EPlayerType.HLS && config.HLSStreamUrl) ||
        (this._currentPlayerType === EPlayerType.NATIVE && config.NativeStreamURL)) &&
      this._domainOverride &&
      this._lastPartOverride
    ) {
      const url: URL = new URL(
        (this._currentPlayerType === EPlayerType.NATIVE
          ? config.NativeStreamURL
          : config.HLSStreamUrl) as string,
      );
      url.host = this._domainOverride;

      const [overridenLastPart, overridenSearch] = this._lastPartOverride.split('?');
      const lastPart: string = url.pathname.split('/').slice(-1)[0];
      url.pathname = url.pathname.replace(lastPart, overridenLastPart);

      if (overridenSearch) {
        url.search = `?${overridenSearch}`;
      }

      if (this._currentPlayerType === EPlayerType.HLS) {
        config.HLSStreamUrl = url.toString();
      } else {
        config.NativeStreamURL = url.toString();
      }
    }

    if (
      this._currentPlayerType === EPlayerType.WEBRTC &&
      config.WebRTCStreamId &&
      config.WebRTCSignallingUrl
    ) {
      config.WebRTCSignallingUrl = this._domainOverride;
      config.WebRTCStreamId = this._lastPartOverride;
    }

    this._eventBusInstance.publish(EPlayersEvents.STREAM_CONFIG_UPDATED_INTERNAL, {
      streamConfig: config,
    });
  }

  private _createPanelRow({ labelText, rowStyle, valueContent }: IDebugPanelRowOptions): IRowPanel {
    const row: HTMLDivElement = document.createElement('div');
    const defaultRowStyle: Partial<CSSStyleDeclaration> = {
      display: 'flex',
      alignItems: 'center',
      margin: '5px 0',
    };

    Object.assign(row.style, rowStyle ?? defaultRowStyle);

    const label: HTMLDivElement = document.createElement('div');
    label.style.minWidth = '130px';
    label.innerHTML = labelText;
    row.appendChild(label);

    const valueElement: HTMLDivElement = document.createElement('div');
    if (valueContent?.innerHTML) {
      valueElement.innerHTML = valueContent?.innerHTML;
    }
    if (valueContent?.style) {
      Object.assign(valueElement.style, valueContent?.style);
    }
    row.appendChild(valueElement);

    this._panelContainer.appendChild(row);
    return {
      setRowValue: (html: string): void => {
        valueElement.innerHTML = html;
      },
      appendRowChild: (child: Node): void => {
        valueElement.appendChild(child);
      },
      getRowChildren: (): NodeListOf<ChildNode> => {
        return valueElement.childNodes;
      },
      addRowEventListener: <K extends keyof HTMLElementEventMap>(
        type: K,
        listener: (this: HTMLDivElement, ev: HTMLElementEventMap[K]) => unknown,
        options?: boolean | AddEventListenerOptions,
      ): void => {
        valueElement.addEventListener(type, listener, options);
      },
      removeRowEventListener: <K extends keyof HTMLElementEventMap>(
        type: K,
        listener: (this: HTMLDivElement, ev: HTMLElementEventMap[K]) => unknown,
        options?: boolean | EventListenerOptions,
      ): void => {
        valueElement.removeEventListener(type, listener, options);
      },
    };
  }

  private _resetOverridenValues(): void {
    if (!this._currentStreamConfig || !this._currentPlayerType) {
      return;
    }

    if (
      (this._currentPlayerType === EPlayerType.HLS && this._currentStreamConfig.HLSStreamUrl) ||
      (this._currentPlayerType === EPlayerType.NATIVE && this._currentStreamConfig.NativeStreamURL)
    ) {
      const url: URL = new URL(
        (this._currentPlayerType === EPlayerType.NATIVE
          ? this._currentStreamConfig.NativeStreamURL
          : this._currentStreamConfig.HLSStreamUrl) as string,
      );

      this._domainOverrideInput.value = url.host;
      this._domainOverride = url.host;

      const lastPart: string = url.pathname.split('/').slice(-1)[0];

      this._streamNameOverrideInput.value = `${lastPart}${url.search}`;
      this._lastPartOverride = lastPart;
    }

    if (
      this._currentStreamConfig.WebRTCSignallingUrl &&
      this._currentStreamConfig.WebRTCStreamId &&
      this._currentPlayerType === EPlayerType.WEBRTC
    ) {
      this._domainOverrideInput.value = this._currentStreamConfig.WebRTCSignallingUrl;
      this._domainOverride = this._currentStreamConfig.WebRTCSignallingUrl;

      this._streamNameOverrideInput.value = this._currentStreamConfig.WebRTCStreamId;
      this._lastPartOverride = this._currentStreamConfig.WebRTCStreamId;
    }
  }

  @bind
  private _onStreamConfigUpdated({
    streamConfig,
  }: TPlayersEventsPayload<EPlayersEvents.STREAM_CONFIG_UPDATED>): void {
    this._currentStreamConfig = streamConfig;
    this._resetOverridenValues();
  }

  @bind
  private _onVideoElementUpdated({
    videoElement,
  }: TPlayersEventsPayload<EPlayersEvents.VIDEO_ELEMENT_UPDATED>): void {
    this._videoElement = videoElement;
    this._prevFramesCount = -1;
  }

  @bind
  private _onPlayerTypesAvailable({
    availablePlayerTypes,
  }: TPlayersEventsPayload<EPlayersEvents.PLAYER_TYPES_AVAILABLE>): void {
    this._availablePlayerTypes = availablePlayerTypes;

    [...this._playerTypesRow.getRowChildren()].forEach((childButton: HTMLElement) => {
      childButton.removeEventListener('click', this._onPlayerTypeButtonClicked);
    });

    this._playerTypesRow.setRowValue('');

    this._availablePlayerTypes.forEach((playerType) => {
      const playerTypeButton: HTMLDivElement = document.createElement('div');
      playerTypeButton.style.border = '1px solid';
      playerTypeButton.style.borderRadius = '5px';
      playerTypeButton.style.padding = '5px 10px';
      playerTypeButton.style.marginRight = '10px';
      playerTypeButton.innerText = playerType;
      playerTypeButton.dataset.playerType = playerType;

      playerTypeButton.addEventListener('click', this._onPlayerTypeButtonClicked);

      this._playerTypesRow.appendRowChild(playerTypeButton);
    });
  }

  @bind
  private _refreshDebugData(): void {
    const videoElementMetrics: IVideoElementMetrics = this._hydrateVideoElementMetrics();
    this._resolutionRow.setRowValue(
      `Video: ${videoElementMetrics.videoResolution};<br />Container: ${videoElementMetrics.elementResolution}<br /> DPR container: ${videoElementMetrics.dprElementResolution}`,
    );
    this._aspectRatioRow.setRowValue(
      `Video: ${videoElementMetrics.videoAspectRatio}; Container: ${videoElementMetrics.elementAspectRatio}`,
    );
    this._fpsRow.setRowValue(videoElementMetrics.fps);
    this._droppedFramesRow.setRowValue(videoElementMetrics.framesCount);

    this._renderCurrentPlayerType();
  }

  private _hydrateVideoElementMetrics(): IVideoElementMetrics {
    if (!this._videoElement) {
      return {
        videoResolution: 'N/A',
        elementResolution: 'N/A',
        dprElementResolution: 'N/A',
        videoAspectRatio: 'N/A',
        elementAspectRatio: 'N/A',
        fps: 'N/A',
        framesCount: 'N/A',
      };
    }
    
    const playbackQuality: VideoPlaybackQuality = getVideoPlaybackQuality(
      this._videoElement,
    );

    const { videoWidth, videoHeight } = this._videoElement;
    const { width, height } = this._lastKnownVideoElementDimensions;
    let fps: string = 'N/A';

    if (playbackQuality) {
      if (this._prevFramesCount > -1) {
        fps = `${Math.round(
          (playbackQuality.totalVideoFrames - this._prevFramesCount) /
            ((Date.now() - this._prevFramesCountTs) / 1000),
        )}`;
      }
      this._prevFramesCount = playbackQuality.totalVideoFrames;
      this._prevFramesCountTs = Date.now();
    }
    return {
      videoResolution: `${videoWidth}x${videoHeight}`,
      elementResolution: `${width}x${height}`,
      dprElementResolution: `${width * window.devicePixelRatio}x${
        height * window.devicePixelRatio
      }`,
      videoAspectRatio:
        videoWidth && videoHeight ? `${Math.round((videoWidth / videoHeight) * 100) / 100}` : 'N/A',
      elementAspectRatio: width && height ? `${Math.round((width / height) * 100) / 100}` : 'N/A',
      fps,
      framesCount: playbackQuality
        ? `${playbackQuality.totalVideoFrames}/${playbackQuality.droppedVideoFrames}`
        : 'N/A',
    };
  }

  private _renderCurrentPlayerType(): void {
    [...this._playerTypesRow.getRowChildren()].forEach((playerTypeButton: HTMLElement) => {
      if (playerTypeButton.dataset.playerType === this._currentPlayerType) {
        playerTypeButton.style.background = '#1491ff';
      } else {
        playerTypeButton.style.background = 'inherit';
      }
    });
  }

  @bind
  private _toggleTURNServers(): void {
    this._shouldUseTURNServersState = !this._shouldUseTURNServersState;
    this._eventBusInstance.publish(EPlayersEvents.TOGGLE_TURN_SERVERS, {
      shouldUseTURNServers: this._shouldUseTURNServersState,
    });

    this._turnServersRow.setRowValue(
      this._shouldUseTURNServersState ? 'Disable TURN Servers' : 'Enable TURN servers',
    );
  }

  @bind
  private _onPlayerTypeButtonClicked(e: MouseEvent): void {
    const targetElement: HTMLElement = e.target as HTMLElement;
    this._eventBusInstance.publish(EPlayersEvents.FORCE_PLAYER_TYPE, {
      playerType: targetElement.dataset.playerType as EPlayerType,
    });
  }

  @bind
  private _onPlayerTypeChanged({
    playerType,
  }: TPlayersEventsPayload<EPlayersEvents.PLAYER_TYPE_CHANGED>): void {
    this._currentPlayerType = playerType;
    this._resetOverridenValues();
  }

  @bind
  private _onLatencyEstimationAvailable({
    medianLatency,
    EWMALatency,
  }: TPlayersEventsPayload<EPlayersEvents.LATENCY_ESTIMATION_AVAILABLE>): void {
    this._latencyRow.setRowValue(`${medianLatency.toFixed(2)} med, ${EWMALatency.toFixed(2)} EWMA`);
  }

  @bind
  private _onUpscaleCalculated({
    upscaleFactor,
  }: TMetricsEventPayload<EMetricsEvents.UPSCALE_CALCULATED>): void {
    this._upscaleRow.setRowValue(upscaleFactor !== null ? upscaleFactor.toFixed(4) : 'N/A');
  }

  @bind
  private _onVideoElementDimensionsCalculated({
    width,
    height,
  }: TMetricsEventPayload<EMetricsEvents.VIDEO_ELEMENT_DIMENSIONS_CALCULATED>): void {
    this._lastKnownVideoElementDimensions = { width, height };
  }

  get rootContainer(): HTMLDivElement {
    return this._panelContainer;
  }
}
